import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { API_PATHS } from '../config/api-paths';
import { SmePaymentsApi } from '../tokens/sme-payments.token';
import { extractBackendErrorMessage } from '../util/backend-error';
import {
  PaymentCategory,
  PaymentDirectoryEntry,
  PaymentInitiationRequest,
  PaymentInitiationResponse,
  PaymentOrderDto,
  PaymentSubmitOutcome,
  RefundableOrder,
  RefundOutcome,
  RefundResponse,
} from '../models/sme-payment.model';

/**
 * REST-only implementation of `SmePaymentsApi` (EM-356, C3).
 *
 * Calls:
 *   POST /services/embedded/api/payments                          (X-SCA-Token when external rail)
 *   GET  /services/embedded/api/payments/directory?category&q
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). Rule 07 / Rule 10 — no Mock
 * fallback; Playwright specs intercept via `page.route()`.
 *
 * `submit` maps the recognised RFC-7807 problem `code`s to a typed outcome so the New Payment wizard
 * can branch without re-parsing the error: `sca_required` → open the SCA modal + re-submit with the
 * minted token; `over_role_limit` / `read_only_role` → translated role-block message; the card
 * `feature_disabled` reject → the disabled message (Rule 09).
 */
@Injectable()
export class SmePaymentsRestService implements SmePaymentsApi {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);
  private readonly base = API_PATHS.PAYMENTS;

  submit(payload: PaymentInitiationRequest, scaToken?: string): Observable<PaymentSubmitOutcome> {
    const headers = scaToken ? new HttpHeaders({ 'X-SCA-Token': scaToken }) : undefined;
    return this.http.post<PaymentInitiationResponse>(this.base, payload, { headers }).pipe(
      map((response): PaymentSubmitOutcome => ({ kind: 'success', response })),
      catchError((err: HttpErrorResponse) => of(this.toOutcome(err))),
    );
  }

  directorySearch(category: PaymentCategory, q: string): Observable<PaymentDirectoryEntry[]> {
    const params = { category: category.toUpperCase(), q: q ?? '' };
    return this.http
      .get<PaymentDirectoryEntry[]>(`${this.base}/directory`, { params })
      .pipe(catchError(() => of([] as PaymentDirectoryEntry[])));
  }

  getById(paymentId: number): Observable<PaymentOrderDto> {
    return this.http.get<PaymentOrderDto>(`${this.base}/${paymentId}`);
  }

  refundable(): Observable<RefundableOrder[]> {
    return this.http
      .get<RefundableOrder[]>(this.base, { params: { refundable: 'true' } })
      .pipe(catchError(() => of([] as RefundableOrder[])));
  }

  refund(paymentId: number, rationale?: string): Observable<RefundOutcome> {
    return this.http.post<RefundResponse>(`${this.base}/${paymentId}/refund`, { rationale: rationale ?? null }).pipe(
      map((response): RefundOutcome => ({ kind: 'success', response })),
      catchError((err: HttpErrorResponse) => of(this.toRefundOutcome(err))),
    );
  }

  /** Maps a failed refund to a typed outcome, keying on the RFC-7807 `code` property. */
  private toRefundOutcome(err: HttpErrorResponse): RefundOutcome {
    const code: string | undefined = err?.error?.code;
    const errorKey: string | undefined = err?.error?.message;
    // EM-1601 — the translator argument is what turns the backend's `error.<key>` into the customer
    // sentence in ro.json / en.json. Without it `extractBackendErrorMessage` HUMANISES the key, which
    // is how "Grace withdrawal failed" / "Psd2 topup init failed" reached the customer verbatim.
    const message = extractBackendErrorMessage(
      err,
      this.transloco.translate('smeBorrower.payments.refund.errors.refundFailed'),
      key => this.transloco.translate(key),
    );

    if (code === 'past_window' || errorKey === 'error.refundWindowExpired') {
      return { kind: 'past_window', errorMessage: message };
    }
    if (err.status === 403 && (code === 'read_only_role' || errorKey === 'error.readOnlyRole')) {
      return { kind: 'read_only_role', errorMessage: message };
    }
    return { kind: 'error', errorMessage: message };
  }

  /** Maps a failed submit to a typed outcome, keying on the RFC-7807 `code` property. */
  private toOutcome(err: HttpErrorResponse): PaymentSubmitOutcome {
    const code: string | undefined = err?.error?.code;
    const errorKey: string | undefined = err?.error?.message; // e.g. "error.scaRequired"
    // EM-1601 — see toRefundOutcome: pass the translator so a rail failure reads as a customer
    // message, never as a humanised internal error key.
    const message = extractBackendErrorMessage(
      err,
      this.transloco.translate('smeBorrower.payments.errors.submitFailed'),
      key => this.transloco.translate(key),
    );

    if (err.status === 403 && (code === 'over_role_limit' || errorKey === 'error.overRoleLimit')) {
      return { kind: 'over_role_limit', errorMessage: message };
    }
    if (err.status === 403 && (code === 'read_only_role' || errorKey === 'error.readOnlyRole')) {
      return { kind: 'read_only_role', errorMessage: message };
    }
    if (err.status === 403 && errorKey === 'error.scaRequired') {
      return { kind: 'sca_required', errorMessage: message };
    }
    if (code === 'feature_disabled' || errorKey === 'error.cardPaymentDisabled') {
      return { kind: 'feature_disabled', errorMessage: message };
    }
    return { kind: 'error', errorMessage: message };
  }
}
